import type { Dayjs } from "dayjs";
import dayjs from "dayjs";

import { shortMonthDayAndShortYear } from "@similarweb/pro-logic-components";

import type { DatePickerPreset } from "app/@types/ISwSettings";
import { swSettings } from "app/common/services/swSettings";
import {
    CAMPAIGNS_DURATION_ONE_WEEK_ID,
    CAMPAIGNS_MONTHS_RANGE_INTERVALS,
    MAX_ALLOWED_DURATION_DEMO_CAMPAIGN,
} from "app/components/campaigns/queryBar-report/reports-pages-filters/durationPicker/CampaignDurationPicker.consts";
import { translate } from "app/filters/i18nFilter";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";

export const getDisplayTextForRelativePickedDurationForCampaigns = (
    durationObject: DatesRange,
    timeRange: number,
): string => {
    const isLastWeekID = timeRange === 7;
    if (isLastWeekID) {
        return translate("datepicker.lastn", {
            count: "7",
            unit: translate("datepicker.units.day.nomerous"),
        });
    }
    const unit =
        timeRange > 1 ? translate("datepicker.units.month.nomerous") : translate("datepicker.units.month.single");
    const presetText = `${timeRange} ${unit}`;
    const endDate = durationObject.to;
    const endDateText = endDate.format(shortMonthDayAndShortYear);
    return `${endDate
        .subtract(timeRange, "month")
        .format(shortMonthDayAndShortYear)} - ${endDateText}  (${presetText})`;
};

/** @param campaignDataStart - First scrape day (see getRtCampaignDataStartDate), not raw createdTime alone */
export const getAllTimeDateRangeForCampaign = (campaignDataStart: Dayjs, maxDate?: string): DatesRange => {
    const etlDate = swSettings.components.RankTracker?.resources?.LastAvailableRankTrackerEtlDate as string;
    const to = dayjs.utc(maxDate ?? etlDate);
    return {
        from: campaignDataStart,
        to,
    };
};

export const isAllTimeSelectedForCampaign = (
    durationObject: DatesRange | undefined,
    campaignDataStart: Dayjs,
    maxDate: string,
): boolean => {
    if (!durationObject?.from?.isValid() || !durationObject?.to?.isValid()) {
        return false;
    }
    const utcMaxDate = dayjs.utc(maxDate);
    const isSameFrom = durationObject.from.isSame(campaignDataStart, "day");
    const isSameTo = durationObject.to.isSame(utcMaxDate, "day");
    return isSameFrom && isSameTo;
};

export const getCalendarPresetsForCampaigns = (
    durationObject: DatesRange,
    isDemoCampaign: boolean,
): DatePickerPreset[] => {
    const dailyRangePickerPresets: DatePickerPreset[] = [];
    const displayTextForOneWeek = getDisplayTextForRelativePickedDurationForCampaigns(durationObject, 7);
    const isMoreThan3Months = durationObject.to.diff(durationObject.from, "month") > 3;
    const allTimeValue =
        isMoreThan3Months && isDemoCampaign
            ? MAX_ALLOWED_DURATION_DEMO_CAMPAIGN
            : `${getTimeDiffBetweenDatesUTC(durationObject) ?? 0}d`;
    dailyRangePickerPresets.push({
        buttonText: displayTextForOneWeek,
        displayText: displayTextForOneWeek,
        value: CAMPAIGNS_DURATION_ONE_WEEK_ID,
        enabled: true,
    });
    CAMPAIGNS_MONTHS_RANGE_INTERVALS.forEach((month) => {
        const unit =
            month > 1 ? translate("datepicker.units.month.nomerous") : translate("datepicker.units.month.single");
        const displayText = getDisplayTextForRelativePickedDurationForCampaigns(durationObject, month);
        dailyRangePickerPresets.push({
            buttonText: translate("datepicker.lastn", {
                count: month.toString(),
                unit,
            }),
            displayText,
            value: month + "m",
            enabled: true,
        });
    });
    dailyRangePickerPresets.push({
        buttonText: translate("datepicker.allTime"),
        displayText: translate("datepicker.allTime"),
        value: allTimeValue,
        enabled: true,
    });
    return dailyRangePickerPresets;
};

export const getTimeDiffBetweenDatesUTC = (durationObject: DatesRange): number | undefined => {
    if (durationObject.from?.isValid() && durationObject.to?.isValid()) {
        return durationObject.to?.utc().diff(durationObject.from?.utc(), "day");
    }
};
